import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from '../Common_Files/Config';
import axios from 'axios';

const generateUniqueFilename = (baseName, extension) => {
  const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
  return `${baseName}_${timestamp}.${extension}`;
};

const generateCollectionNotePDF = async (id) => {
  const accessToken = localStorage.getItem('accessToken');
  const formData = new FormData();
  formData.set('sale_id', id);
  const apiUrl = `${config.api_url}/ws-get-sales-by-id`;

  try {
    const response = await axios.post(apiUrl, formData, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });
    const data = await response.data;
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    let y = margin;
    
    // Function to add new page if needed
    const checkPageBreak = (requiredHeight) => {
      if (y + requiredHeight > pageHeight - margin - 20) {
        doc.addPage();
        y = margin;
        return true;
      }
      return false;
    };
    
    // Function to add page numbers
    const addPageNumbers = () => {
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8).setFont('helvetica', 'normal');
        doc.text(`Page ${i}/${totalPages}`, pageWidth - margin, pageHeight - margin, { align: 'right' });
      }
    };

    const wrapText = (text, maxWidth) => doc.splitTextToSize(text || '', maxWidth);

    const loadImageAsDataUrl = async (src) => {
      try {
        const res = await fetch(src, { cache: 'no-store' });
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };

    const logoDataUrl = await loadImageAsDataUrl('/assets/img/logo.jpg');
    const logoWidth = 28;
    const logoHeight = 12;
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'JPEG', margin, y, logoWidth, logoHeight);
    }

    // Company address
    const leftX = margin + logoWidth + 5;
    doc.setFontSize(12).setFont('helvetica', 'bold');
    doc.text('Star Pacific UK LTD', leftX, y + 4);

    const companyAddress = [
      'Unit 6 Abenglen Industrial Estate,',
      'Betam Road, Hayes',
      'Middlesex, London UB3 1AT',
      'United Kingdom'
    ];
    let smallLineHeight = 4;
    doc.setFontSize(8).setFont('helvetica', 'normal');
    companyAddress.forEach((line, idx) => {
      doc.text(line, leftX, y + 4 + smallLineHeight * (idx + 1));
    });

    // Collection address
    let addressTitle = 'Collection Address:';
    let addressLines = [
      data?.sale?.address_line1 || '',
      data?.sale?.address_line2 || '',
      data?.sale?.city || '',
      data?.sale?.post_code || '',
      data?.sale?.country || ''
    ].filter(Boolean);

    let addrStartY = y + 4 + (smallLineHeight * (companyAddress.length + 1));
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text(addressTitle, leftX - 25, addrStartY);
    const addressValueStartX = leftX - 25 + doc.getTextWidth(addressTitle + ' ');
    doc.setFontSize(8).setFont('helvetica', 'normal');
    
    let collectionY = addrStartY;
    let totalAddressLines = 0;
    if (addressLines.length > 0) {
      addressLines.forEach((line, idx) => {
        const wrappedLines = wrapText(line, 60);
        wrappedLines.forEach((wrappedLine, lineIdx) => {
          doc.text(wrappedLine, addressValueStartX, collectionY);
          collectionY += smallLineHeight;
          totalAddressLines++;
        });
      });
    }

    // Right header - Collection Note
    const rightX = pageWidth - margin;
    doc.setFontSize(16).setFont('helvetica', 'bold');
    doc.setTextColor(255, 99, 71); // Tomato color (RGB: 255, 99, 71)
    doc.text('Collection Note', rightX, y - 1, { align: 'right' });
    doc.setTextColor(0, 0, 0); // Reset to black color

    // Center-justified company registration info (positioned to the right of company address)
    const regInfoStartX = leftX + 50; // Position to the right of company address
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text('Co. Reg. No.: 7298396', regInfoStartX, y + 4);
    doc.text('VAT Reg. No.: GB994533184', regInfoStartX, y + 4 + smallLineHeight);
    doc.text('Tel: 44-208 329 3800', regInfoStartX, y + 4 + smallLineHeight * 2);

    // Helper function to format booking time (show empty if 00:00:00)
    const formatBookingTime = (time) => {
      if (!time || time === '00:00:00' || time.trim() === '00:00:00') {
        return '';
      }
      return time;
    };

    // Helper function to format collection date (show empty if 0000-00-00)
    const formatCollectionDate = (date) => {
      if (!date || date === '0000-00-00' || date.trim() === '0000-00-00') {
        return '';
      }
      return date;
    };

    // Right-aligned supplier info (positioned in top-right area)
    const bookingTime = formatBookingTime(data?.sale?.delivery_time);
    const collectionDate = formatCollectionDate(data?.sale?.delivery_date);
    
    const supplierInfo = [
      `Supplier PO: ${data?.sale?.customer_ref || ''}`,
      `Booking Ref: ${data?.sale?.booking_ref || ''}`,
      `Booking Time: ${bookingTime}`,
      `Collection Date: ${collectionDate}`,
      `SO#: ${data?.sale?.reference_no || ''}`
    ];

    // Prepare wrapped Collection Note similar to Delivery Note
    const previewNote = `Collection Note: ${data?.sale?.notes || ''}`;
    const previewMaxWidth = 34;
    const previewMaxLines = 100;
    let previewLines = wrapText(previewNote, previewMaxWidth);
    if (previewLines.length > previewMaxLines) {
      previewLines = previewLines.slice(0, previewMaxLines);
      previewLines.push('...');
    }

    // Position supplier info on the right side, below the header
    let suppStartY = y + 4 + (smallLineHeight * 4); // Below the registration info
    doc.setFontSize(8).setFont('helvetica', 'normal');
    supplierInfo.forEach((line, idx) => {
      doc.text(line, rightX - 37, suppStartY + idx * smallLineHeight, { align: 'left' });
    });
    // Render wrapped collection note below supplier info
    let collectionNoteY = suppStartY + supplierInfo.length * smallLineHeight;
    previewLines.forEach((line, lineIdx) => {
      doc.text(line, rightX - 37, collectionNoteY + lineIdx * smallLineHeight, { align: 'left' });
    });

    const totalRightInfoHeight = (supplierInfo.length + previewLines.length) * smallLineHeight;
    y = Math.max(addrStartY + (totalAddressLines * smallLineHeight), suppStartY + totalRightInfoHeight) + 6;

    // Table
    const columns = [
      'Line No', 'SKU Code', 'Product Description', 'Pack', 'Size', 'Unit Barcode', 'Case Qty', 'Pallet Qty'
    ];

    const tableBody = data.product.map((item, index) => {
      return [
        index + 1, // Row number
        item.code, // Product code
        wrapText(item.product_desc, 50).join('\n'), // Wrapped description
        item.pack, // Pack info
        item.size, // Size
        item.unit_barcode, // Barcode
        item.salesQty, // Qty
        item.pallet_qty // Pallet qty
      ];
    });

    // Totals
    const totalQty = data.product.reduce(
      (acc, item) => acc + Number(item.salesQty || 0),
      0
    );

    const totalPalletQty = data.product.reduce(
      (acc, item) => acc + Number(item.pallet_qty || 0),
      0
    );

    // Add totals row
    tableBody.push([
      '', '', '', '', '', 'Total:',
      totalQty.toFixed(2),
      totalPalletQty.toFixed(0)
    ]);

    doc.autoTable({
      startY: y,
      head: [columns],
      body: tableBody,
      theme: 'grid',
      headStyles: { fillColor: '#f8f9fa', textColor: '#404040', fontSize: 8, lineWidth: 0.09, lineColor: '#000', lineStyle: 'solid' },
      bodyStyles: { fontSize: 7, lineHeight: 1.5, lineColor: '#000', lineWidth: 0.09 },
      margin: { top: 10, left: margin, right: margin },
      styles: { overflow: 'linebreak', cellWidth: 'wrap', halign: 'left' },
      columnStyles: {
        0: { cellWidth: 12, halign: 'center' }, // Line No
        1: { cellWidth: 20, halign: 'left' }, // SKU Code
        2: { cellWidth: 50, halign: 'left' }, // Product Description - wider for long descriptions
        3: { cellWidth: 15, halign: 'center' }, // Pack
        4: { cellWidth: 15, halign: 'center' }, // Size
        5: { cellWidth: 25, halign: 'left' }, // Unit Barcode
        6: { cellWidth: 20, halign: 'right' }, // Case Qty
        7: { cellWidth: 20, halign: 'right' }, // Pallet Qty
      },
      didDrawPage: function (data) {
        // Add header on each page if needed
        if (data.pageNumber > 1) {
          // You can add repeating header here if needed
        }
      }
    });

    // Footer margin top
    y = doc.autoTable.previous.finalY + 15; // added more margin

    // Helpers like Delivery Note to render footer columns
    const measureColumnsHeight = (columnsArr) => {
      const adjustedWidth = (pageWidth - margin * 2) / columnsArr.length;
      let maxH = 0;
      columnsArr.forEach((col) => {
        let h = 0;
        col.forEach((item) => {
          const lines = wrapText(item.text, adjustedWidth - 2);
          h += lines.length * 4.5;
        });
        if (h > maxH) maxH = h;
      });
      return maxH;
    };

    const renderColumns = (columnsArr) => {
      const adjustedWidth = (pageWidth - margin * 2) / columnsArr.length;
      let maxColBottom = y;
      columnsArr.forEach((col, idx) => {
        const colStartX = margin + idx * adjustedWidth;
        let colY = y;
        col.forEach((item) => {
          // Check if we need a new page before adding this item
          const wrapped = wrapText(item.text, adjustedWidth - 2);
          const itemHeight = wrapped.length * 4.5;
          if (colY + itemHeight > pageHeight - margin - 20) {
            doc.addPage();
            colY = margin;
            maxColBottom = margin;
          }
          doc.setFontSize(item.size - 0.5);
          doc.setFont(item.bold ? 'helvetica' : 'helvetica', item.bold ? 'bold' : 'normal');
          wrapped.forEach((line) => {
            doc.text(line, colStartX, colY);
            colY += 4.5;
          });
        });
        if (colY > maxColBottom) maxColBottom = colY;
      });
      y = maxColBottom + 12;
    };

    const gross_weight = data.product.reduce(
      (acc, item) => acc + Number(item.gross_weight || 0),
      0
    );

    const cbm = data.product.reduce(
      (acc, item) => acc + Number(item.cbm || 0),
      0
    );

    const net_weight = data.product.reduce(
      (acc, item) => acc + Number(item.net_weight || 0),
      0
    );

    const footerColumns = [
      [
        { text: `Notes: ${data?.sale?.notes || ''}`, size: 8, bold: false },
        { text: `Ref: ${data?.sale?.job_ref || data?.sale?.refno || ''}`, size: 8, bold: false },
      ],
      [{ text: '', size: 8, bold: false }],
      [
        { text: `Gross Weight: ${gross_weight.toFixed(2)} KGS`, size: 8, bold: false },
        { text: `CBM: ${cbm.toFixed(2)} M3`, size: 8, bold: false },
        { text: `Net Weight: ${net_weight.toFixed(2)} KGS`, size: 8, bold: false },
      ],
    ];

    // Check if we need a new page before pallet table
    checkPageBreak(50);
    
    // Pallet types table and footer sections in one horizontal row
    const tableStartY = y + 10;
    const palletColumns = ['Pallet Type', ''];
    const palletData = [
      ['CHEP Standard', totalPalletQty.toFixed(0)],
      ['LPR Standard', ''],
      ['IPP Standard', ''],
      ['CHEP Euro', ''],
      ['Others', '']
    ];

    // Left side - Pallet table
    doc.autoTable({
      startY: tableStartY,
      body: palletData,
      theme: 'grid',
      headStyles: { fillColor: '#f8f9fa', textColor: '#404040', fontSize: 8, lineWidth: 0.09, lineColor: '#000', lineStyle: 'solid' },
      bodyStyles: { fontSize: 8, lineColor: '#000', lineWidth: 0.09 },
      margin: { top: 10 },
      tableWidth: 40,
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 10 }
      },
      didDrawPage: function (data) {
        if (data.pageNumber > 1) {
          y = margin;
        }
      }
    });

    // Right side - Driver and Warehouse sections side by side
    const rightStartX = margin + 50; // Start after pallet table
    const driverStartX = rightStartX;
    const warehouseStartX = rightStartX + 80; // Warehouse section starts 80mm to the right
    let rightY = tableStartY + 10;

    // Driver section (left side)
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text('Driver', driverStartX, rightY);
    doc.setFontSize(8).setFont('helvetica', 'normal');
    doc.text('Loaded in good condition and correct pallet count', driverStartX, rightY + 5);
    doc.text('Name -------------------------------', driverStartX, rightY + 10);
    doc.text('Sign -------------------------------', driverStartX, rightY + 15);

    // Warehouse section (right side) - same Y position
    doc.setFontSize(8).setFont('helvetica', 'bold');
    doc.text('Warehouse', warehouseStartX, rightY);
    doc.setFontSize(8).setFont('helvetica', 'normal');
    doc.text('Visual inspection of pallets and product carried out', warehouseStartX, rightY + 5);
    doc.text('Name -------------------------------', warehouseStartX, rightY + 10);
    doc.text('Sign -------------------------------', warehouseStartX, rightY + 15);

    const combinedEndY = Math.max(doc.autoTable.previous.finalY, rightY + 20);

    const footerSections3 = [
      [
        { text: 'Subject to Star Pacific terms and conditions of sale, a copy of which is available on request', size: 8, bold: false },
        { text: 'Star Pacific (No. 7298396)', size: 8, bold: false },
        { text: 'Reservation of Title. The property in and the title to all goods sold by Star Pacific shall not pass to the buyer until all monies due and owing have been paid in full', size: 8, bold: false },
      ]
    ];

    // Continue from the combined table and footer sections
    y = Math.max(combinedEndY, doc.autoTable.previous?.finalY || combinedEndY) + 10;

    // Render footerColumns similar to Delivery Note with space check and reserved bottom space
    const reservedBottom = 28; // space for bottom disclaimers
    const needed = measureColumnsHeight(footerColumns);
    if (y + needed > pageHeight - margin - reservedBottom) {
      doc.addPage();
      y = margin;
    }
    renderColumns(footerColumns);

    // Check if we need a new page for footer sections
    const footerHeight = footerSections3[0].length * 5 + 10;
    if (y + footerHeight > pageHeight - margin - 10) {
      doc.addPage();
      y = margin;
    }
    
    const bottomY = pageHeight - margin - (footerSections3[0].length * 5);
    footerSections3.forEach((section) => {
      const centerX = pageWidth / 2;
      section.forEach((item, i) => {
        wrapText(item.text, pageWidth - 20).forEach((line, lineIdx) => {
          doc.setFontSize(item.size);
          doc.setFont(item.bold ? 'helvetica' : 'helvetica', item.bold ? 'bold' : 'normal');
          doc.text(line, centerX, bottomY + (i + lineIdx) * 5, { align: 'center' });
        });
      });
    });

    // Add dynamic page numbering (call after all pages are created)
    addPageNumbers();

    const pdfBlob = doc.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    window.open(blobUrl, '_blank');
  } catch (error) {
    console.error('Error generating PDF:', error);
  }
};

export default generateCollectionNotePDF;
